import { get as httpGet, put as httpPut } from '@/services/http';

export const CANONICAL_COLUMN_IDS = ['image', 'unitCostBase', 'unitCost', 'totalCost'] as const;

// AS-11685 (parent AS-11682 D3): live Shopify stock columns. Same ids as atlas
// AS-11684 — the config is merchant-wide and shared by both apps.
export const LIVE_STOCK_COLUMN_IDS = ['liveAvailable', 'liveOnHand', 'liveOnHandAfterReceive'] as const;
export type LiveStockColumnId = typeof LIVE_STOCK_COLUMN_IDS[number];

// An explicit literal, NOT derived from the toggleable list: deriving it would
// turn the live columns on by default (D2: they are off).
export const DEFAULT_VISIBLE_COLUMNS: string[] = ['image', 'unitCostBase', 'unitCost', 'totalCost'];

const COLUMN_LABELS: Record<string, string> = {
  image: 'Image',
  unitCostBase: 'Unit Cost (Base)',
  unitCost: 'Unit Cost',
  totalCost: 'Total Cost',
  liveAvailable: 'Available',
  liveOnHand: 'On hand',
  liveOnHandAfterReceive: 'On hand after receive',
};

// Every id the picker can toggle and filterReceiveHeaders can hide: the fixed
// columns, then the live stock columns.
const TOGGLEABLE_COLUMN_IDS: readonly string[] = [...CANONICAL_COLUMN_IDS, ...LIVE_STOCK_COLUMN_IDS];

// Product, Status, and Receive Qty are locked and never part of this
// vocabulary at all (they're hardcoded into receiveTableHeaders, not subject
// to this picker).
export const TOGGLEABLE_COLUMN_LABELS: { id: string; label: string }[] = TOGGLEABLE_COLUMN_IDS
  .map((id) => ({ id, label: COLUMN_LABELS[id] }));

// Canonical id -> this app's `headers[].value`. Ids not listed map 1:1
// (unitCostBase, unitCost, totalCost).
export const WARBLER_RECEIVE_COLUMN_VALUE_MAP: Record<string, string> = {
  image: 'variant_image',
};

const HEADER_VALUE_TO_CANONICAL_ID: Record<string, string> = Object.fromEntries(
  Object.entries(WARBLER_RECEIVE_COLUMN_VALUE_MAP).map(([canonicalId, headerValue]) => [headerValue, canonicalId]),
);

function canonicalIdForHeaderValue(headerValue: string): string {
  return HEADER_VALUE_TO_CANONICAL_ID[headerValue] ?? headerValue;
}

// Filters a headers array down to visibleColumns, but only for headers whose
// canonical id is toggleable (CANONICAL_COLUMN_IDS or LIVE_STOCK_COLUMN_IDS). Any other header (locked
// columns like productName/quantityStatus/receiveQuantity, or dynamic
// custom_* headers the caller already built conditionally) passes through
// untouched.
export function filterReceiveHeaders<T extends { value: string }>(allHeaders: T[], visibleColumns: string[]): T[] {
  return allHeaders.filter((header) => {
    const canonicalId = canonicalIdForHeaderValue(header.value);
    if (!TOGGLEABLE_COLUMN_IDS.includes(canonicalId)) return true;
    return visibleColumns.includes(canonicalId);
  });
}

interface GetDep { get: (url: string, params?: any) => Promise<any>; }
interface PutDep { put: (url: string, body?: any) => Promise<any>; }

export async function fetchReceiveColumnsConfig(deps: GetDep = { get: httpGet }): Promise<string[]> {
  try {
    const response: any = await deps.get('/purchase_orders/config/receive-columns');
    const visibleColumns = response?.data?.visibleColumns;
    return Array.isArray(visibleColumns) ? visibleColumns : [...DEFAULT_VISIBLE_COLUMNS];
  } catch {
    return [...DEFAULT_VISIBLE_COLUMNS];
  }
}

export async function saveReceiveColumnsConfig(visibleColumns: string[], deps: PutDep = { put: httpPut }): Promise<void> {
  await deps.put('/purchase_orders/config/receive-columns', { visibleColumns });
}
